<template>
  <input
    ref="input"
    :value="displayValue"
    type="text"
    class="form-control"
    :placeholder="computedPlaceholder"
    :disabled="disabled"
    @input="handleInput"
    @keypress="validateKeypress"
    @focus="handleFocus"
    @blur="handleBlur"
  />
</template>

<script>
export default {
  name: 'NumericalInput',
  props: {
    modelValue: {
      type: [Number, null],
      default: null
    },
    optional: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    computedPlaceholder() {
      if (this.placeholder) {
        return this.placeholder;
      }
      return this.optional ? 'Leave empty for no default' : '';
    },
    displayValue() {
      if (this.modelValue === null) {
        return '';
      }
      // Return raw value without formatting to allow easy typing
      return this.modelValue;
    }
  },
  methods: {
    handleFocus(event) {
      // Auto-select all text for easy overwriting
      event.target.select();
    },
    handleBlur() {
      this.$emit('blur');
    },
    validateKeypress(event) {
      const key = event.key;
      const currentValue = event.target.value;

      // Allow numbers
      if (/^\d$/.test(key)) {
        return;
      }

      // Allow decimal point only if there isn't one already
      if (key === '.' && !currentValue.includes('.')) {
        return;
      }

      // Allow minus sign only at the beginning
      if (key === '-' && currentValue.length === 0) {
        return;
      }

      // Prevent all other characters
      event.preventDefault();
    },
    handleInput(event) {
      let value = event.target.value;

      // Strip any invalid characters that might have been pasted
      value = value.replace(/[^\d.-]/g, '');

      // Ensure only one decimal point
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }

      // Ensure only one minus sign at the beginning
      if (value.indexOf('-') > 0) {
        value = value.replace(/-/g, '');
      }

      // Update the input if we cleaned it
      if (value !== event.target.value) {
        event.target.value = value;
      }

      // If empty and optional, emit null
      if (value === '' && this.optional) {
        this.$emit('update:modelValue', null);
        return;
      }

      // If empty and not optional, emit 0
      if (value === '') {
        this.$emit('update:modelValue', 0);
        return;
      }

      // Allow intermediate states like "30." or "-" while typing
      // Don't emit anything for these incomplete values
      if (value === '-' || value === '.' || value.endsWith('.')) {
        // Don't emit, just allow the user to continue typing
        return;
      }

      // Parse the number (handles both integers and decimals)
      const parsedValue = parseFloat(value);

      // Emit the parsed value if valid
      if (!isNaN(parsedValue)) {
        this.$emit('update:modelValue', parsedValue);
      }
    }
  },
  emits: ['update:modelValue', 'blur']
}
</script>

<style scoped>
/* Inherits .form-control styles from global.css */
</style>
